import { Box, Button, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Stack } from '@mui/material';
import styled from 'styled-components';
import { ReactNode } from 'react';

// Styled Components
export const QuestionContainer = styled(Box)`
  margin-bottom: 32px;
  padding: 24px;
  background: #2a3441;
  border-radius: 12px;
  border: 1px solid #3a4a5c;
`;

export const QuestionTitle = styled(Typography)`
  color: #fff;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 1.1rem;
`;

export const QuestionSubtitle = styled(Typography)`
  color: #b0b8c1;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

export const StyledButton = styled(Button) <{ variant?: 'primary' | 'secondary' | 'outline' }>`
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  font-size: 1rem;
  
  ${props => props.variant === 'primary' && `
    background: #377dff;
    color: #fff;
    &:hover {
      background: #2563eb;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #4a5568;
    color: #fff;
    &:hover {
      background: #2d3748;
    }
  `}
  
  ${props => props.variant === 'outline' && `
    background: transparent;
    color: #377dff;
    border: 1px solid #377dff;
    &:hover {
      background: rgba(55, 125, 255, 0.1);
    }
  `}
`;

export const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    background: #1a202c;
    color: #fff;
    border-radius: 8px;
    
    & fieldset {
      border-color: #4a5568;
    }
    
    &:hover fieldset {
      border-color: #377dff;
    }
    
    &.Mui-focused fieldset {
      border-color: #377dff;
      border-width: 2px;
    }
  }
  
  & .MuiInputLabel-root {
    color: #b0b8c1;
    
    &.Mui-focused {
      color: #377dff;
    }
  }
`;

export const StyledSelect = styled(Select)`
  background: #1a202c;
  color: #fff;
  border-radius: 8px;
  
  & .MuiOutlinedInput-notchedOutline {
    border-color: #4a5568;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #377dff;
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #377dff;
    border-width: 2px;
  }
  
  & .MuiSvgIcon-root {
    color: #b0b8c1;
  }
`;

// Question Components
interface YesNoQuestionProps {
  title: string;
  subtitle: string | React.ReactNode;
  value: boolean | null;
  onChange: (value: boolean) => void;
  required?: boolean;
}

export const YesNoQuestion = ({
  title,
  subtitle,
  value,
  onChange,
  required = false
}: YesNoQuestionProps) => {
  return (
    <QuestionContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="column" justifyContent="flex-start" alignItems="flex-start">
          <QuestionTitle>
            {title}
            {required && <span style={{ color: '#ef4444' }}> *</span>}
          </QuestionTitle>
          <QuestionSubtitle>{subtitle}</QuestionSubtitle>
        </Stack>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button sx={{borderColor:"white", backgroundColor: value === true ? '#377dff' : '#232933', color: value === true ? '#fff' : '#b0b8c1' }} variant={value === true ? 'contained' : 'outlined'} color="primary" onClick={() => onChange(true)}>Yes</Button>
          <Button sx={{borderColor:"white", backgroundColor: value === false ? '#377dff' : '#232933', color: value === false ? '#fff' : '#b0b8c1' }} variant={value === false ? 'contained' : 'outlined'} color="primary" onClick={() => onChange(false)}>No</Button>
        </Box>
      </Stack>
    </QuestionContainer>
  );
};

interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  multiline?: boolean;
  rows?: number;
}

export const FormField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  type = 'text',
  multiline = false,
  rows = 1
}: FormFieldProps) => {
  return (
    <Box sx={{ mb: 3 }}>
      <StyledTextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        type={type}
        multiline={multiline}
        rows={rows}
        variant="outlined"
      />
    </Box>
  );
};

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}

export const SelectField = ({
  label,
  value,
  onChange,
  options,
  required = false
}: SelectFieldProps) => {
  return (
    <Box sx={{ mb: 3 }}>
      <FormControl fullWidth required={required}>
        <InputLabel sx={{ color: '#b0b8c1', '&.Mui-focused': { color: '#377dff' } }}>
          {label}
        </InputLabel>
        <StyledSelect
          value={value}
          onChange={(e) => onChange(e.target.value as string)}
          label={label}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ color: '#fff', backgroundColor: '#232933' }}>
              {option.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
    </Box>
  );
};

interface UploadZoneProps {
  title: string;
  subtitle: string;
  onUpload: (file: File) => void;
  acceptedTypes: string;
  icon?: ReactNode;
}

export const UploadZone = ({
  title,
  subtitle,
  onUpload,
  acceptedTypes,
  icon
}: UploadZoneProps) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <Box
      sx={{
        border: '2px dashed #4a5568',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        backgroundColor: '#1a202c',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
          borderColor: '#377dff',
          backgroundColor: '#2a3441'
        }
      }}
      component="label"
    >
      <input
        type="file"
        accept={acceptedTypes}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {icon && (
        <Box sx={{ mb: 1, color: '#377dff', fontSize: '2rem' }}>
          {icon}
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>

      <Typography variant="body2" sx={{ color: '#b0b8c1', mb: 2, fontSize: '0.875rem' }}>
        {subtitle}
      </Typography>

      {/* <StyledButton variant="primary" size="small" sx={{ px: 3, py: 1 }}>
        Upload File
      </StyledButton> */}
    </Box>
  );
};

interface NavigationButtonsProps {
  onNext?: () => void;
  onPrevious?: () => void;
  nextLabel?: string;
  previousLabel?: string;
  canProceed?: boolean;
  isLastStep?: boolean;
  loading?: boolean;
}

export const NavigationButtons = ({
  onNext,
  onPrevious,
  nextLabel = 'Next',
  previousLabel = 'Previous',
  canProceed = true,
  isLastStep = false,
  loading = false
}: NavigationButtonsProps) => {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mt: 'auto',
      pt: 3
    }}>
      {onPrevious ? (
        <Button variant="outlined" onClick={onPrevious}>
          ← {previousLabel}
        </Button>
      ) : (
        <Box />
      )}

      {onNext && (
        <Button
          variant="contained"
          color="primary"
          onClick={onNext}
          disabled={!canProceed || loading}
        >
          {loading ? 'Loading...' : isLastStep ? 'Complete' : `${nextLabel} →`}
        </Button>
      )}
    </Box>
  );
};

interface InfoCardProps {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
}

export const InfoCard = ({ title, children, icon }: InfoCardProps) => {
  return (
    <Box
      sx={{
        background: '#2a3441',
        border: '1px solid #3a4a5c',
        borderRadius: 2,
        p: 3,
        mb: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon && (
          <Box sx={{ mr: 2, color: '#377dff' }}>
            {icon}
          </Box>
        )}
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ color: '#b0b8c1' }}>
        {children}
      </Box>
    </Box>
  );
};
